import React, { useState, useEffect } from 'react';
import {
  AIAgent,
  AgentSpecialization,
  ABTestAgentExecution,
  AgentCollaboration,
  AgentAlert,
  AutomationLevel,
  RiskLevel
} from '../../../types/aiAgent';
import { aiAgentService } from '../../../services/aiAgentService';
import { AIAgentConfiguration } from './AIAgentConfiguration';
import { AIAgentPerformanceMonitor } from './AIAgentPerformanceMonitor';
import { AIAgentApprovalWorkflow } from './AIAgentApprovalWorkflow';
import { AIAgentTraining } from './AIAgentTraining';
import { NotificationSystem } from './NotificationSystem';
import { NavigationProvider } from './NavigationBridge';
import { NavigationHeader } from './NavigationHeader';
import { AgentCollaborationDashboard } from './AgentCollaborationDashboard';
import { tone, toneForStatus } from "components/ui/tone";
import { clickable } from "components/ui/interactive";
import { SelectField } from "components/ui/select-field";

interface AIAgentDashboardProps {
  onAgentSelect?: (agent: AIAgent) => void;
  onExecutionSelect?: (execution: ABTestAgentExecution) => void;
  standalone?: boolean; // Whether to include navigation wrapper
}

export const AIAgentDashboard: React.FC<AIAgentDashboardProps> = ({
  onAgentSelect,
  onExecutionSelect
}) => {
  const [agents, setAgents] = useState<AIAgent[]>([]);
  const [activeExecutions, setActiveExecutions] = useState<ABTestAgentExecution[]>([]);
  const [collaborations, setCollaborations] = useState<AgentCollaboration[]>([]);
  const [alerts, setAlerts] = useState<AgentAlert[]>([]);
  
  // Agent collaboration data
  const [communications, setCommunications] = useState<any[]>([]);
  const [conflicts, setConflicts] = useState<any[]>([]);
  const [learningTransfers, setLearningTransfers] = useState<any[]>([]);
  const [benchmarks, setBenchmarks] = useState<any[]>([]);
  const [selectedTab, setSelectedTab] = useState<'overview' | 'agents' | 'executions' | 'collaborations' | 'alerts' | 'configuration' | 'performance' | 'approval' | 'training'>('overview');
  const [loading, setLoading] = useState(true);
  const [showCreateAgent, setShowCreateAgent] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AIAgent | null>(null);
  const [_showConfiguration, _setShowConfiguration] = useState(false);
  const [_showPerformanceMonitor, _setShowPerformanceMonitor] = useState(false);
  const [_showTraining, _setShowTraining] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [agentsData, executionsData] = await Promise.all([
        aiAgentService.getAllAgents(),
        // TODO: Load executions from API
        Promise.resolve([])
      ]);

      setAgents(agentsData);
      setActiveExecutions(executionsData);
      setCollaborations([]); // TODO: Load from API
      setAlerts([]); // TODO: Load from API
      
      // Initialize collaboration data
      setCommunications([]);
      setConflicts([]);
      setLearningTransfers([]);
      setBenchmarks([]);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async (specialization: AgentSpecialization, config: any) => {
    try {
      const newAgent = await aiAgentService.createAgent(specialization, config);
      setAgents(prev => [...prev, newAgent]);
      setShowCreateAgent(false);
    } catch (error) {
      console.error('Failed to create agent:', error);
    }
  };

  const _getStatusColor = (status: string) => {
    const colors = {
      idle: 'bg-surface-sunken text-content',
      active: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      busy: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
      error: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
      paused: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      learning: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
    };
    return colors[status as keyof typeof colors] || 'bg-surface-sunken text-content';
  };

  const _getSpecializationIcon = (specialization: AgentSpecialization) => {
    const icons = {
      ab_test_optimizer: '🧪',
      performance_analyzer: '📊',
      campaign_creator: '🚀',
      subject_line_expert: '✍️',
      template_designer: '🎨',
      statistical_analyst: '📈',
      content_optimizer: '📝',
      audience_segmentation: '🎯'
    };
    return icons[specialization] || '🤖';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <NavigationProvider>
      <div className="min-h-screen bg-gray-50 dark:bg-dark-700">
        {/* Navigation Header */}
        <NavigationHeader
          title="AI Agent Dashboard"
          subtitle="Monitor and manage autonomous A/B testing agents"
          currentItem={{
            type: 'agent',
            name: 'AI Agent Dashboard',
            path: '/ab-testing/agents'
          }}
          actions={
            <button
              onClick={() => setShowCreateAgent(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Agent
            </button>
          }
        />

        <div className="bg-surface rounded-lg shadow-sm border border-line max-w-7xl mx-auto mt-6">
          {/* System Overview */}
          <div className="border-b border-line px-6 py-4">
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-300">{agents.length}</div>
                <div className="text-sm text-blue-600 dark:text-blue-300">Active Agents</div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
                <div className="text-2xl font-bold text-green-600 dark:text-green-300">{activeExecutions.length}</div>
                <div className="text-sm text-green-600 dark:text-green-300">Running Executions</div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-300">{collaborations.length}</div>
                <div className="text-sm text-purple-600 dark:text-purple-300">Collaborations</div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-4">
                <div className="text-2xl font-bold text-red-600 dark:text-red-300">{alerts.length}</div>
                <div className="text-sm text-red-600 dark:text-red-300">Active Alerts</div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs - Compact Design */}
          <div className="border-b border-line">
            <nav className="px-6">
              <div className="flex space-x-1 bg-gray-100 dark:bg-dark-700 rounded-lg p-1 max-w-fit">
                {[
                  { id: 'overview', label: 'Overview', icon: '📊' },
                  { id: 'agents', label: 'Agents', icon: '🤖' },
                  { id: 'configuration', label: 'Config', icon: '⚙️' },
                  { id: 'performance', label: 'Performance', icon: '📈' },
                  { id: 'approval', label: 'Approval', icon: '✅' },
                  { id: 'training', label: 'Training', icon: '🎓' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedTab(tab.id as any)}
                    className={`px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-2 transition-colors ${selectedTab === tab.id
                        ? ' text-blue-600 dark:text-blue-300 shadow-sm'
                        : 'text-content-secondary hover:text-content hover:bg-gray-50 dark:hover:bg-dark-700'
                      }`}
                  >
                    <span className="text-xs">{tab.icon}</span>
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>

              {/* Secondary tabs for less common actions */}
              <div className="flex space-x-4 mt-3 pb-3">
                {[
                  { id: 'executions', label: 'Executions', icon: '⚡' },
                  { id: 'collaborations', label: 'Collaborations', icon: '🤝' },
                  { id: 'alerts', label: 'Alerts', icon: '🚨' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedTab(tab.id as any)}
                    className={`text-sm flex items-center space-x-1 px-2 py-1 rounded transition-colors ${selectedTab === tab.id
                        ? 'text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30'
                        : 'text-content-muted hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                  >
                    <span className="text-xs">{tab.icon}</span>
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            </nav>
          </div>

          {/* Tab Content */}
          <div className={selectedTab === 'overview' ? 'p-6' : ''}>
            {selectedTab === 'overview' && (
              <OverviewTab agents={agents} executions={activeExecutions} />
            )}

            {selectedTab === 'agents' && (
              <div className="p-6">
                <AgentsTab
                  agents={agents}
                  onAgentSelect={(agent) => {
                    setSelectedAgent(agent);
                    onAgentSelect?.(agent);
                  }}
                />
              </div>
            )}

            {selectedTab === 'configuration' && selectedAgent && (
              <AIAgentConfiguration
                agent={selectedAgent}
                onConfigurationUpdate={(agentId, config) => {
                  setAgents(prev => prev.map(agent =>
                    agent.id === agentId ? { ...agent, configuration: config } : agent
                  ));
                }}
                onAgentUpdate={(agentId, updates) => {
                  setAgents(prev => prev.map(agent =>
                    agent.id === agentId ? { ...agent, ...updates } : agent
                  ));
                }}
                onClose={() => setSelectedAgent(null)}
              />
            )}

            {selectedTab === 'configuration' && !selectedAgent && (
              <div className="p-6">
                <div className="text-center py-12">
                  <div className="text-content-subtle text-6xl mb-4">⚙️</div>
                  <h3 className="text-lg font-medium text-content mb-2">Select an Agent to Configure</h3>
                  <p className="text-content-secondary mb-4">Choose an agent from the Agents tab to configure its settings</p>
                  <button
                    onClick={() => setSelectedTab('agents')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Go to Agents
                  </button>
                </div>
              </div>
            )}

            {selectedTab === 'performance' && selectedAgent && (
              <AIAgentPerformanceMonitor
                agent={selectedAgent}
                onAlertAction={(alertId, action) => {
                  console.log(`Alert ${alertId} ${action}`);
                }}
              />
            )}

            {selectedTab === 'performance' && !selectedAgent && (
              <div className="p-6">
                <div className="text-center py-12">
                  <div className="text-content-subtle text-6xl mb-4">📈</div>
                  <h3 className="text-lg font-medium text-content mb-2">Select an Agent to Monitor</h3>
                  <p className="text-content-secondary mb-4">Choose an agent from the Agents tab to view its performance metrics</p>
                  <button
                    onClick={() => setSelectedTab('agents')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Go to Agents
                  </button>
                </div>
              </div>
            )}

            {selectedTab === 'approval' && (
              <AIAgentApprovalWorkflow
                onDecisionApproved={(decisionId, approved, feedback) => {
                  console.log(`Decision ${decisionId} ${approved ? 'approved' : 'rejected'}`, feedback);
                }}
                onBulkAction={(decisionIds, action) => {
                  console.log(`Bulk ${action} for decisions:`, decisionIds);
                }}
              />
            )}

            {selectedTab === 'training' && selectedAgent && (
              <AIAgentTraining
                agent={selectedAgent}
                onCapabilityUpdate={(agentId, capabilities) => {
                  setAgents(prev => prev.map(agent =>
                    agent.id === agentId ? { ...agent, capabilities } : agent
                  ));
                }}
                onTrainingComplete={(agentId, results) => {
                  console.log(`Training completed for agent ${agentId}:`, results);
                }}
              />
            )}

            {selectedTab === 'training' && !selectedAgent && (
              <div className="p-6">
                <div className="text-center py-12">
                  <div className="text-content-subtle text-6xl mb-4">🎓</div>
                  <h3 className="text-lg font-medium text-content mb-2">Select an Agent to Train</h3>
                  <p className="text-content-secondary mb-4">Choose an agent from the Agents tab to enhance its capabilities</p>
                  <button
                    onClick={() => setSelectedTab('agents')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Go to Agents
                  </button>
                </div>
              </div>
            )}

            {selectedTab === 'executions' && (
              <div className="p-6">
                <ExecutionsTab executions={activeExecutions} onExecutionSelect={onExecutionSelect} />
              </div>
            )}

            {selectedTab === 'collaborations' && (
              <AgentCollaborationDashboard
                collaborations={collaborations}
                communications={communications}
                conflicts={conflicts}
                learningTransfers={learningTransfers}
                benchmarks={benchmarks}
                onCreateCollaboration={(request) => {
                  console.log('Create collaboration:', request);
                }}
                onResolveConflict={(conflictId) => {
                  console.log('Resolve conflict:', conflictId);
                }}
                onShareKnowledge={(knowledge) => {
                  console.log('Share knowledge:', knowledge);
                }}
              />
            )}

            {selectedTab === 'alerts' && (
              <NotificationSystem
                notifications={[]}
                currentUser={{
                  id: 'current-user',
                  name: 'Current User',
                  email: 'user@company.com',
                  role: 'admin',
                  permissions: ['view_test', 'manage_agents'],
                  status: 'active',
                  joinedAt: new Date(),
                  lastActive: new Date(),
                  preferences: {
                    notifications: { email: true, inApp: true, types: ['agent_decision'] },
                    timezone: 'UTC',
                    language: 'en'
                  },
                  metadata: {}
                }}
                onMarkAsRead={(notificationId) => {
                  console.log(`Mark notification ${notificationId} as read`);
                }}
                onMarkAllAsRead={() => {
                  console.log('Mark all notifications as read');
                }}
                onDismiss={(notificationId) => {
                  console.log(`Dismiss notification ${notificationId}`);
                }}
                onAction={(notificationId, actionId) => {
                  console.log(`Notification ${notificationId} action ${actionId}`);
                }}
              />
            )}
          </div>

          {/* Create Agent Modal */}
          {showCreateAgent && (
            <CreateAgentModal
              onClose={() => setShowCreateAgent(false)}
              onCreate={handleCreateAgent}
            />
          )}
        </div>
      </div>
    </NavigationProvider>
  );
};

// Overview Tab Component
const OverviewTab: React.FC<{ agents: AIAgent[]; executions: ABTestAgentExecution[] }> = ({
  agents,
  executions: _executions
}) => {
  const getPerformanceMetrics = () => {
    const totalTasks = agents.reduce((sum, agent) => sum + agent.performance.overall.tasksCompleted, 0);
    const avgSuccessRate = agents.length > 0
      ? agents.reduce((sum, agent) => sum + agent.performance.overall.successRate, 0) / agents.length
      : 0;
    const avgImprovement = agents.length > 0
      ? agents.reduce((sum, agent) => sum + agent.performance.overall.improvementRate, 0) / agents.length
      : 0;

    return { totalTasks, avgSuccessRate, avgImprovement };
  };

  const getSpecializationIcon = (specialization: AgentSpecialization) => {
    const icons = {
      ab_test_optimizer: '🧪',
      performance_analyzer: '📊',
      campaign_creator: '🚀',
      subject_line_expert: '✍️',
      template_designer: '🎨',
      statistical_analyst: '📈',
      content_optimizer: '📝',
      audience_segmentation: '🎯'
    };
    return icons[specialization] || '🤖';
  };

  // Agent-specific states that the global map does not know about; everything
  // else falls through to the shared status→tone mapping.
  const getStatusColor = (status: string) =>
    tone(
      status === 'busy' ? 'warning'
      : status === 'learning' ? 'violet'
      : status === 'idle' ? 'neutral'
      : toneForStatus(status)
    ).chip;

  const { totalTasks, avgSuccessRate, avgImprovement } = getPerformanceMetrics();

  return (
    <div className="space-y-6">
      {/* Performance Metrics */}
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <h3 className="text-lg font-semibold mb-2">Total Tasks Completed</h3>
          <div className="text-3xl font-bold">{totalTasks}</div>
          <div className="text-blue-100 text-sm mt-1">Across all agents</div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
          <h3 className="text-lg font-semibold mb-2">Average Success Rate</h3>
          <div className="text-3xl font-bold">{(avgSuccessRate * 100).toFixed(1)}%</div>
          <div className="text-green-100 text-sm mt-1">System-wide performance</div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <h3 className="text-lg font-semibold mb-2">Average Improvement</h3>
          <div className="text-3xl font-bold">{(avgImprovement * 100).toFixed(1)}%</div>
          <div className="text-purple-100 text-sm mt-1">Performance gains</div>
        </div>
      </div>

      {/* Agent Status Overview */}
      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Agent Status Distribution</h3>
        <div className="grid grid-cols-6 gap-4">
          {['idle', 'active', 'busy', 'error', 'paused', 'learning'].map(status => {
            const count = agents.filter(agent => agent.status === status).length;
            return (
              <div key={status} className="text-center">
                <div className="text-2xl font-bold text-content">{count}</div>
                <div className="text-sm text-content-secondary capitalize">{status}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Recent Agent Activity</h3>
        <div className="space-y-3">
          {agents.slice(0, 5).map(agent => (
            <div key={agent.id} className="flex items-center justify-between p-4 bg-surface border border-line rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="text-2xl">{getSpecializationIcon(agent.specialization)}</div>
                <div>
                  <div className="font-medium text-content">{agent.name}</div>
                  <div className="text-sm text-content-secondary">
                    {agent.currentTasks.length} active tasks
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(agent.status)}`}>
                  {agent.status}
                </span>
                <div className="text-sm text-content-secondary">
                  {(agent.performance.overall.successRate * 100).toFixed(0)}% success
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Agents Tab Component
const AgentsTab: React.FC<{ agents: AIAgent[]; onAgentSelect?: (agent: AIAgent) => void }> = ({
  agents,
  onAgentSelect
}) => {
  const getSpecializationIcon = (specialization: AgentSpecialization) => {
    const icons = {
      ab_test_optimizer: '🧪',
      performance_analyzer: '📊',
      campaign_creator: '🚀',
      subject_line_expert: '✍️',
      template_designer: '🎨',
      statistical_analyst: '📈',
      content_optimizer: '📝',
      audience_segmentation: '🎯'
    };
    return icons[specialization] || '🤖';
  };

  // Agent-specific states that the global map does not know about; everything
  // else falls through to the shared status→tone mapping.
  const getStatusColor = (status: string) =>
    tone(
      status === 'busy' ? 'warning'
      : status === 'learning' ? 'violet'
      : status === 'idle' ? 'neutral'
      : toneForStatus(status)
    ).chip;

  return (
    <div className="space-y-4">
      {agents.map(agent => (
        <div
          key={agent.id}
          className="bg-surface border border-line rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
          {...clickable(() => onAgentSelect?.(agent))}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="text-3xl">{getSpecializationIcon(agent.specialization)}</div>
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-content">{agent.name}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(agent.status)}`}>
                    {agent.status}
                  </span>
                </div>
                <p className="text-content-secondary mt-1">{agent.metadata.description}</p>

                {/* Performance Metrics */}
                <div className="grid grid-cols-4 gap-4 mt-4">
                  <div>
                    <div className="text-sm text-content-muted">Success Rate</div>
                    <div className="font-semibold text-content">
                      {(agent.performance.overall.successRate * 100).toFixed(1)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-content-muted">Tasks Completed</div>
                    <div className="font-semibold text-content">
                      {agent.performance.overall.tasksCompleted}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-content-muted">Avg Improvement</div>
                    <div className="font-semibold text-content">
                      {(agent.performance.overall.improvementRate * 100).toFixed(1)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-content-muted">Active Tasks</div>
                    <div className="font-semibold text-content">
                      {agent.currentTasks.length}
                    </div>
                  </div>
                </div>

                {/* Capabilities */}
                <div className="mt-4">
                  <div className="text-sm text-content-muted mb-2">Top Capabilities</div>
                  <div className="flex flex-wrap gap-2">
                    {agent.capabilities.slice(0, 3).map(capability => (
                      <span
                        key={capability.id}
                        className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded-full"
                      >
                        {capability.name} ({capability.proficiency}%)
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="text-right">
              <div className="text-sm text-content-muted">Automation Level</div>
              <div className="font-semibold text-content capitalize">
                {agent.automationLevel}
              </div>
            </div>
          </div>
        </div>
      ))}

      {agents.length === 0 && (
        <div className="text-center py-12">
          <div className="text-content-subtle text-6xl mb-4">🤖</div>
          <h3 className="text-lg font-medium text-content mb-2">No agents created yet</h3>
          <p className="text-content-secondary">Create your first AI agent to start autonomous A/B testing</p>
        </div>
      )}
    </div>
  );
};

// Executions Tab Component
const ExecutionsTab: React.FC<{
  executions: ABTestAgentExecution[];
  onExecutionSelect?: (execution: ABTestAgentExecution) => void
}> = ({ executions, onExecutionSelect }) => {
  const getExecutionStatusColor = (status: string) => {
    const colors = {
      pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
      running: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      completed: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      failed: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
      paused: 'bg-surface-sunken text-content'
    };
    return colors[status as keyof typeof colors] || 'bg-surface-sunken text-content';
  };

  return (
    <div className="space-y-4">
      {executions.map(execution => (
        <div
          key={execution.id}
          className="bg-surface border border-line rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
          {...clickable(() => onExecutionSelect?.(execution))}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-lg font-semibold text-content">
                  {execution.executionType.replace('_', ' ').toUpperCase()}
                </h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getExecutionStatusColor(execution.status)}`}>
                  {execution.status}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-content-muted">Agent ID</div>
                  <div className="font-medium">{execution.agentId.slice(0, 8)}...</div>
                </div>
                <div>
                  <div className="text-content-muted">Test ID</div>
                  <div className="font-medium">{execution.testId.slice(0, 8)}...</div>
                </div>
                <div>
                  <div className="text-content-muted">Started</div>
                  <div className="font-medium">
                    {execution.startedAt.toLocaleDateString()}
                  </div>
                </div>
              </div>

              {execution.results.confidence > 0 && (
                <div className="mt-4">
                  <div className="text-sm text-content-muted mb-1">Confidence Score</div>
                  <div className="w-full bg-gray-200 dark:bg-dark-600 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${execution.results.confidence * 100}%` }}
                    ></div>
                  </div>
                  <div className="text-sm text-content-secondary mt-1">
                    {(execution.results.confidence * 100).toFixed(1)}%
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {executions.length === 0 && (
        <div className="text-center py-12">
          <div className="text-content-subtle text-6xl mb-4">⚡</div>
          <h3 className="text-lg font-medium text-content mb-2">No active executions</h3>
          <p className="text-content-secondary">Agent executions will appear here when tests are running</p>
        </div>
      )}
    </div>
  );
};



// Alerts Tab Component
const _AlertsTab: React.FC<{ alerts: AgentAlert[] }> = ({ alerts }) => {
  return (
    <div className="space-y-4">
      {alerts.length === 0 && (
        <div className="text-center py-12">
          <div className="text-content-subtle text-6xl mb-4">🔔</div>
          <h3 className="text-lg font-medium text-content mb-2">No active alerts</h3>
          <p className="text-content-secondary">System alerts and notifications will appear here</p>
        </div>
      )}
    </div>
  );
};

// Create Agent Modal Component
const CreateAgentModal: React.FC<{
  onClose: () => void;
  onCreate: (specialization: AgentSpecialization, config: any) => void;
}> = ({ onClose, onCreate }) => {
  const [specialization, setSpecialization] = useState<AgentSpecialization>('ab_test_optimizer');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [automationLevel, setAutomationLevel] = useState<AutomationLevel>('supervised');
  const [riskTolerance, setRiskTolerance] = useState<RiskLevel>('medium');

  const specializations = [
    { value: 'ab_test_optimizer', label: 'A/B Test Optimizer', icon: '🧪' },
    { value: 'performance_analyzer', label: 'Performance Analyzer', icon: '📊' },
    { value: 'campaign_creator', label: 'Campaign Creator', icon: '🚀' },
    { value: 'subject_line_expert', label: 'Subject Line Expert', icon: '✍️' },
    { value: 'template_designer', label: 'Template Designer', icon: '🎨' },
    { value: 'statistical_analyst', label: 'Statistical Analyst', icon: '📈' },
    { value: 'content_optimizer', label: 'Content Optimizer', icon: '📝' },
    { value: 'audience_segmentation', label: 'Audience Segmentation', icon: '🎯' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate(specialization, {
      name: name || undefined,
      automationLevel,
      configuration: {
        riskTolerance,
        // Other default config will be set by service
      },
      metadata: {
        description: description || undefined
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-line">
          <h3 className="text-lg font-semibold text-content">Create AI Agent</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Specialization
            </label>
            <SelectField
              value={specialization}
              onChange={(e) => setSpecialization(e.target.value as AgentSpecialization)}
              className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {specializations.map(spec => (
                <option key={spec.value} value={spec.value}>
                  {spec.icon} {spec.label}
                </option>
              ))}
            </SelectField>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Name (optional)
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Custom agent name"
              className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Agent description"
              rows={3}
              className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Automation Level
            </label>
            <SelectField
              value={automationLevel}
              onChange={(e) => setAutomationLevel(e.target.value as AutomationLevel)}
              className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="manual">Manual</option>
              <option value="assisted">Assisted</option>
              <option value="supervised">Supervised</option>
              <option value="autonomous">Autonomous</option>
            </SelectField>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Risk Tolerance
            </label>
            <SelectField
              value={riskTolerance}
              onChange={(e) => setRiskTolerance(e.target.value as RiskLevel)}
              className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </SelectField>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-line-strong rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Agent
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};